import { app } from '@/composition/app'
import { ConversationState } from '@/composition/conversations'
import { GroupState } from '@/composition/groups'
import config from '@/config'
import { icons } from '@/config/channelIcons'
import { NO_GROUP_SLUG } from '@/helper/const'
import { loadImage } from '@/helper/ImageUpload.helper'
import { IUser } from '@/models/Users'
import { AppLauncher } from '@capacitor/app-launcher'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import { isGokollab } from '@ghl/clientportal-core/init'
import { FunctionalComponent, HTMLAttributes, VNodeProps } from 'vue'
import { RouteLocationNormalizedLoaded, Router } from 'vue-router'

export enum AttachmentSource {
  POSTS = 'posts',
  COMMENTS = 'comments',
}

export async function openLinkInBrowser(resourceUrl: string): Promise<void> {
  if (!resourceUrl) return
  if (app.state.isMobile && Capacitor.isNativePlatform()) {
    const { value: canOpenWithNativeApp } = await AppLauncher.canOpenUrl({
      url: resourceUrl,
    })
    if (canOpenWithNativeApp) {
      await AppLauncher.openUrl({ url: resourceUrl })
      return
    }
  }
  await Browser.open({ url: resourceUrl })
}

export async function redirectToSignUpPage(redirectUrl = ''): Promise<void> {
  window.location.href = `${config.CLIENTPORTAL_PREVIEW_URL}/sign-up?redirectUrl=${redirectUrl}`
}

export async function redirectToLoginPage(redirectUrl = ''): Promise<void> {
  window.location.href = `${config.CLIENTPORTAL_PREVIEW_URL}/login?otpLogin=true&&redirectUrl=${redirectUrl}`
}

export function redirectToNoCommunitiesPage(router: Router): void {
  router.push({
    name: app.state.isMobile ? 'no-communities-mobile' : 'no-communities',
    params: { groupSlug: NO_GROUP_SLUG },
  })
}

export function formatDate(inputDateString: string): string {
  const date = new Date(inputDateString)
  const options: any = { day: '2-digit', month: 'short', year: 'numeric' }
  const outputDateString = date.toLocaleDateString('en-GB', options)
  return outputDateString
}

export function formatDateMonthYear(inputDateString: string): string {
  const date = new Date(inputDateString)
  const options: any = { month: 'short', year: 'numeric' }
  const outputDateString = date.toLocaleDateString('en-GB', options)
  return outputDateString
}

export function formatSize(size: any): string {
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
  let l = 0,
    n = parseInt(size, 10) || 100
  while (n >= 1000 && ++l) {
    n = n / 1000
  }
  return n.toFixed(l > 0 ? 2 : 0) + ' ' + units[l]
}

export function formatFileType(fileType: string): string {
  const fileTypeWithoutApplication = fileType.replace('application/', '')
  return fileTypeWithoutApplication.toUpperCase()
}

export function getShortName(name: string): string {
  if (name) {
    const nameArray = name.split(/\s+/)
    if (nameArray.length > 1) {
      return (nameArray[0][0] + nameArray[1][0]).toUpperCase()
    } else {
      return nameArray[0][0].toUpperCase()
    }
  }
  return ''
}

export function getOriginalName(modifiedName: string): string | null {
  if (!modifiedName) return null
  const delimiterIndex = modifiedName?.indexOf('comm-media__')
  if (delimiterIndex !== -1) {
    return modifiedName?.substring(delimiterIndex + 21)
  }
  return modifiedName
}

export function getAvatarGroupOption(user: any): any {
  const option = {
    fullName: user.fullName,
    avatar: user?.avatar,
    text: getShortName(user.fullName),
    contactId: user?.contactId,
    slug: user?.slug,
    lastLogin: user?.lastLogin,
    title: user?.title,
    bio: user?.bio,
    gamificationMeta: user?.gamificationMeta,
    className: 'text-communities-font-secondary bg-communities-background text-xxs',
  }
  // if (user?.avatar) {
  //   option.avatar =
  // } else {
  //   option.text =
  // }
  return option
}

export function truncateString(inputString: string, stringSize: number): string {
  if (inputString.length > stringSize) {
    return inputString.substring(0, stringSize) + '..'
  }
  return inputString
}

export function getFirstName(name: string): string {
  if (!name) return ''
  const nameArray = name.split(' ')
  if (nameArray.length > 1) {
    return nameArray[0]
  } else {
    return nameArray[0]
  }
}

export function isChannelIconEmoji(iconName: string): boolean {
  return /\p{Extended_Pictographic}/u.test(iconName)
}

export function getChannelIcon(iconName: string): FunctionalComponent<HTMLAttributes & VNodeProps> | undefined {
  const icon = icons.find(icon => icon.iconName === iconName)
  return icon?.icon
}

export function ColorLuminance(hex: string, lum: number): string {
  if (!hex) return ''
  // validate hex string
  hex = String(hex).replace(/[^0-9a-f]/gi, '')
  if (hex.length < 6) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
  }
  lum = lum || 0

  // convert to decimal and change luminosity
  let rgb = '#',
    c,
    i
  for (i = 0; i < 3; i++) {
    c = parseInt(hex.substr(i * 2, 2), 16)
    c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16)
    rgb += ('00' + c).substr(c.length)
  }
  return rgb
}

export function highlightedText(text: string): string {
  const pattern = /((http|https|ftp):\/\/[^\s]+)/g
  const replacedText = text.replace(pattern, '<a href="$&" target="_blank" class="highlight-link">$&</a>')
  return replacedText
}

export function copyTextToClipboard(text: string): void {
  const textElem = document.createElement('textarea')
  textElem.innerHTML = text
  document.body.appendChild(textElem)
  textElem.select()
  document.execCommand('copy')
  navigator.clipboard.writeText(textElem.value)
  document.body.removeChild(textElem)
}

export function removeEventsListenersFromMentions(
  // eslint-disable-next-line no-undef
  mentionElements: NodeListOf<Element>,
  mentionUserTooltipElement: Element | null
): void {
  mentionElements.forEach(anchor => {
    anchor.removeEventListener('mouseover', event => {
      showUserProfilePreviewTooltip(event.target as Element, mentionUserTooltipElement)
    })

    anchor.removeEventListener('mouseout', () => {
      closeUserProfilePreviewTooltip(mentionUserTooltipElement)
    })
  })
}

export function showUserProfilePreviewTooltip(targetElement: Element | null, parentElement: Element | null): void {
  if (targetElement) {
    const parentElementRect = targetElement?.parentElement?.getBoundingClientRect()
    const linkRects = targetElement?.getClientRects()

    parentElement?.dispatchEvent(
      new CustomEvent('show-tooltip', {
        bubbles: true,
        detail: {
          parent: {
            left: linkRects?.item(0)?.left,
            right: linkRects?.item(0)?.right,
            top: linkRects?.item(0)?.top,
            bottom: linkRects?.item(0)?.bottom,
            height: linkRects?.item(0)?.height,
            width: linkRects?.item(0)?.width,
            parentElementRect: parentElementRect,
          },
          contactId: targetElement.getAttribute('data-id'),
        },
      })
    )
  }
}

export function closeUserProfilePreviewTooltip(parentElement: Element | null): void {
  parentElement?.dispatchEvent(new CustomEvent('close-tooltip', {}))
}

export function openUserProfile(userSlug: string | undefined, route: RouteLocationNormalizedLoaded, router: Router): void {
  if (userSlug) {
    router.push({
      name: 'global-user-profile',
      params: { userSlug: userSlug, groupSlug: route.params.groupSlug },
      query: {
        groupSlug: route.params.groupSlug,
      },
    })
  }
}

export function openGlobalUserProfile(userSlug: string, router: Router, groupId?: string): void {
  const finalGroupId = groupId || GroupState.state?.currentGroup?._id || GroupState.state?.groupData?._id
  router.push({
    name: 'global-user-profile',
    params: { userSlug: userSlug },
    query: {
      groupId: finalGroupId,
    },
  })
}

export async function convertToJPEG(blobedFile: Blob, imageType: string): Promise<string> {
  const img = new Image()
  img.src = URL.createObjectURL(blobedFile)
  await loadImage(img)
  const canvas = document.createElement('canvas')
  canvas.id = 'communities-image-converter-canvas'
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.drawImage(img, 0, 0)
    const jpegUrl = canvas.toDataURL(imageType)
    return jpegUrl
  } else {
    return ''
  }
}

export async function openChatWindow(user?: IUser, router?: Router, conversationId?: string): Promise<void> {
  let conversation
  if (user?.contactId && user.fullName && !conversationId) {
    conversation = await ConversationState.createConversation(user.contactId)
    conversation['recepients'] = [user]
  }
  if (app.state.isMobile && router) {
    router.push({
      name: 'chat-view',
      params: { conversationId: conversation?._id ?? conversationId },
    })
  } else {
    ConversationState.openNewChatWindow(conversation?._id ?? conversationId, conversation)
  }
}

export function extractTextFromHTML(htmlString) {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlString

  return tempDiv.textContent || tempDiv.innerText || ''
}

export function truncateText(text: string, MAX_LENGTH = 25): string {
  if (!text) return ''
  if (text.length <= MAX_LENGTH) return text
  return text.slice(0, MAX_LENGTH) + '...'
}

export function getUserProfileUrl(userSlug: string, groupId?: string): string {
  const base = isGokollab() ? '' : '/communities'
  const query = groupId ? `?groupId=${groupId}` : ''
  return `${base}/users/${userSlug}${query}`
}

export function getChannelUrl(groupSlug: string, channelSlug: string): string {
  if (isGokollab()) {
    return `/${groupSlug}/channels/${channelSlug}`
  }
  return `/communities/groups/${groupSlug}/channels/${channelSlug}`
}

export function getEventUrl(groupSlug: string, eventId: string, eventDate: string): string {
  if (isGokollab()) {
    return `/${groupSlug}/events?eventId=${eventId}&eventDate=${eventDate}`
  }
  return `/communities/groups/${groupSlug}/events?eventId=${eventId}&eventDate=${eventDate}`
}

export function getCourseUrl(groupSlug: string, courseId: string): string {
  if (isGokollab()) {
    return `/${groupSlug}/learning?productId=${courseId}`
  }
  return `/communities/groups/${groupSlug}/learning?productId=${courseId}`
}
